/**
 * **강조된 텍스트를 노드로 그린다** — `dangerouslySetInnerHTML` 없이.
 *
 * 계정명·작성자명(신원 갈래 18자리)이 이것을 쓴다. 그 값들에 마크업이 필요한 이유가 없고,
 * 필요 없는데 HTML 통로를 열어 두면 **글쓴이가 고른 문자열이 태그가 된다.**
 *
 * ```
 *   하이라이터로 «찾고» → DOMPurify 로 씻고 → 조각으로 펴서 → React 가 텍스트로 그린다
 * ```
 *
 * 마지막 단계가 핵심이다 — React 는 텍스트를 **언제나 escape** 하므로, 어떤 입력이 와도
 * 이 자리에서 태그가 만들어질 수 없다. 「세척이 돌았나」를 믿을 필요가 없어진다.
 *
 * 서버와 하이드레이션 첫 그림은 씻기를 건너뛴다(DOM 이 없다 — `SafeHtml` 머리말). 조각이 React 텍스트 노드로
 * 나가므로 그때도 태그가 만들어질 수 없다.
 */

import { useMemo } from 'react';

import { toHighlightSegments } from 'src/utils/text/highlight-segments';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';

import { useSanitizerReady, sanitizeHighlightedHtml } from 'src/components/cmui/post-card/SafeHtml';

import { HighlightSegmentNodes } from './highlight-segment-nodes';

export type HighlightedTextProps = {
  text: string | null | undefined;
  /** 앞에 붙일 글자 — `@계정명` 의 `@` 처럼 **강조 대상이 아닌** 장식 */
  prefix?: string;
  searchKeyword?: string;
  topicKeywords?: string[];
  clientSearch?: string;
  sentimentPositive?: string[];
  sentimentNegative?: string[];
};

export default function HighlightedText({
  text,
  prefix = '',
  searchKeyword = '',
  topicKeywords,
  clientSearch = '',
  sentimentPositive,
  sentimentNegative,
}: HighlightedTextProps) {
  const ready = useSanitizerReady();
  const segments = useMemo(() => {
    if (!text) return [];
    // 🔑 순서가 계약이다 — **찾고 → 씻고 → 조각으로.** 씻기가 마지막 «문자열» 변환이고,
    //    그 뒤로는 React 가 텍스트 노드로만 그리므로 태그가 만들어질 수 없다.
    const marked = highlightTextWithHtmlRemoval(
      String(text),
      searchKeyword,
      topicKeywords ?? [],
      clientSearch,
      sentimentPositive ?? [],
      sentimentNegative ?? [],
    );
    return toHighlightSegments(ready ? sanitizeHighlightedHtml(marked) : marked);
  }, [ready, text, searchKeyword, topicKeywords, clientSearch, sentimentPositive, sentimentNegative]);

  return (
    <>
      {prefix}
      <HighlightSegmentNodes segments={segments} />
    </>
  );
}
